import {Alert, Button, Card, Col, Collapse, Divider, Row, Space, Tag, Tooltip} from "antd";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {useRecoilValue} from "recoil";
import {
    CheckCircleOutlined,
    FileOutlined,
    LockOutlined,
    SafetyCertificateOutlined,
} from "@ant-design/icons";
import {useInitializeUserVerificationHistory} from "../../../../function/accountVerification";
import {verificationRefetchState} from "../../../../state";
import {useIsMobile} from "../../../../../../_common/function";
import {extractLocalizedValue, getStatusColor} from "../../../../common";
import {VerificationStep} from "../../../../common/constant";
import {
    apiCancelVerificationHistory,
    apiDuplicateUserVerificationHistory
} from "../../../../../../api/restful/verification";

function SelectTier(props) {
    const {
        userVerificationHistories,
        goTo,
        setSelectedTier,
        userVerificationTiers,
        accessRightsListCategory,
        blockedByFailedTier,
        failedTierHistory,
        blockedTierHistory,
    } = props

    const {t} = useTranslation();
    const {initializeUserVerificationHistory} = useInitializeUserVerificationHistory();
    const {refetchUserVerificationProfile, refetchUserVerificationHistories} = useRecoilValue(verificationRefetchState);
    const isMobile = useIsMobile()
    const [isCancelling, setIsCancelling] = useState(false)

    const handleCancelBlockedTier = async () => {
        if (!blockedTierHistory?._id) return
        try {
            setIsCancelling(true)
            await apiCancelVerificationHistory(blockedTierHistory._id)
            await Promise.all([
                refetchUserVerificationProfile(),
                refetchUserVerificationHistories(),
            ])
        } catch (e) {
            console.error("handleCancelBlockedTier error:", e)
        } finally {
            setIsCancelling(false)
        }
    }

    const handleSelectTier = async (tier, startNewRecord = false) => {
        setSelectedTier(tier);
        const existingHistory = [...(userVerificationHistories || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).find(history => (history.tierSetting?._id || history.tierSetting) === tier._id)
        const recordExist = ["canceled", "verification_failed"].includes(existingHistory?.status)

        if (!existingHistory?._id || (recordExist && startNewRecord)) {
            try {
                const result = await initializeUserVerificationHistory(tier._id);


                if (result?.status === "Success") {

                    await Promise.all([
                        refetchUserVerificationProfile(),
                        refetchUserVerificationHistories()
                    ]);

                    if (startNewRecord) return

                    const timeout = setTimeout(() => {
                        goTo(VerificationStep.Document);
                    }, 150);

                    return () => clearTimeout(timeout);
                }

            } catch (error) {
                console.error("error initializing verification history:", error);
            }
        } else if (recordExist) {
            try {
                const result = await apiDuplicateUserVerificationHistory({_id: existingHistory?._id})

                if (result?.status === "Success") {
                    await Promise.all([
                        refetchUserVerificationProfile(),
                        refetchUserVerificationHistories()
                    ]);

                    const timeout = setTimeout(() => {
                        goTo(VerificationStep.Document);
                    }, 150);

                    return () => clearTimeout(timeout);
                }
            } catch (e) {
                console.error("error duplicating verification history:", e);
            }
        } else {
            goTo(VerificationStep.Document);
        }
    }

    const getTierStatus = (tierId) => {
        const history = [...(userVerificationHistories || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .find((h) => (h.tierSetting?._id || h.tierSetting) === tierId);
        return history?.status ?? null;
    }

    const getNextTier = (history, tiers) => {
        const sortedHistory = [...(history || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        const validHistory = sortedHistory.find(h => h.status === "pending_verification" || h.status === "verified")
        if (!validHistory) return tiers.find(t => t.tierLevel === 1)

        let nextTierLevel = validHistory.tierLevel + 1

        while (true) {
            const nextTier = tiers.find(t => t.tierLevel === nextTierLevel)
            if (!nextTier) return null

            const nextTierHistory = sortedHistory.find(h => h.tierLevel === nextTierLevel)

            if (
                !nextTierHistory ||
                (nextTierHistory.status !== "pending_verification" && nextTierHistory.status !== "verified")
            ) {
                return nextTier
            }

            nextTierLevel++
        }
    }

    const currentTierAvailable = getNextTier(userVerificationHistories, userVerificationTiers)
    const currentTierAvailableId = currentTierAvailable?._id
    const currentTierLevel = currentTierAvailable?.tierLevel || 0

    const standaloneTier = userVerificationTiers?.find(d => d?.type === "standalone")

    const mapCategoryAccess = (category, access) => {
        const result = {}

        for (const [catKey, catValues] of Object.entries(category || {})) {
            const arr = []

            for (const [accKey, accVal] of access) {
                if (catValues[accKey]) {
                    arr.push([accKey, accVal])
                }
            }

            if (arr.length > 0) {
                result[catKey] = arr
            }
        }

        return result
    }

    if (blockedByFailedTier) {
        const blockedTierSettingId = blockedTierHistory?.tierSetting?._id || blockedTierHistory?.tierSetting
        const failedTierSettingId = failedTierHistory?.tierSetting?._id || failedTierHistory?.tierSetting
        const blockedTier = userVerificationTiers?.find(t => t._id === blockedTierSettingId) || (blockedTierHistory?.tierSetting?._id ? blockedTierHistory.tierSetting : null)
        const failedTier = userVerificationTiers?.find(t => t._id === failedTierSettingId) || (failedTierHistory?.tierSetting?._id ? failedTierHistory.tierSetting : null)
        const blockedTierName = extractLocalizedValue(blockedTier?.displayName) || blockedTier?.name
        const failedTierName = extractLocalizedValue(failedTier?.displayName) || failedTier?.name

        return (
            <Row gutter={[16, 16]}>
                {blockedTier && (
                    <Col span={24}>
                        <Card className="account-verification-card-selection h-full">
                            <Space direction="vertical" size={8} style={{width: "100%"}}>
                                {isMobile && (
                                    <Tag color={getStatusColor(blockedTierHistory.status)} bordered={false}>
                                        {t(`accountVerification.status.${blockedTierHistory.status}`) || blockedTierHistory.status.replace(/_/g, " ")}
                                    </Tag>
                                )}
                                <Row justify="space-between" align="middle">
                                    <div className="account-verification-card-selection-title">{blockedTierName}</div>
                                    <Space className="ms-auto">
                                        {blockedTier?.tierLevel && (
                                            <Tag bordered={false} color="geekblue">{t("Tier")} {blockedTier.tierLevel}</Tag>
                                        )}
                                        {!isMobile && (
                                            <Tag color={getStatusColor(blockedTierHistory.status)} bordered={false}>
                                                {t(`accountVerification.status.${blockedTierHistory.status}`) || blockedTierHistory.status.replace(/_/g, " ")}
                                            </Tag>
                                        )}
                                    </Space>
                                </Row>
                                <Alert
                                    type="warning"
                                    showIcon
                                    message={t("accountVerification.blockedTier.alert", { failedLevel: failedTierHistory.tierLevel, failedTierName, blockedLevel: blockedTierHistory.tierLevel })}
                                />
                                <Row className="mt-2">
                                    <Col xs={24} md={12}>
                                        <Button
                                            danger
                                            block={isMobile}
                                            loading={isCancelling}
                                            onClick={handleCancelBlockedTier}
                                        >
                                            {t("accountVerification.blockedTier.cancelButton", { blockedLevel: blockedTierHistory.tierLevel })}
                                        </Button>
                                    </Col>
                                </Row>
                            </Space>
                        </Card>
                    </Col>
                )}
                {failedTier && (
                    <Col span={24}>
                        <Tooltip title={t("accountVerification.blockedTier.tooltip", { blockedLevel: blockedTierHistory.tierLevel })}>
                            <Card className="account-verification-card-selection h-full" style={{opacity: 0.6}}>
                                <Space direction="vertical" size={8} style={{width: "100%"}}>
                                    {isMobile && (
                                        <Tag color={getStatusColor(failedTierHistory.status)} bordered={false}>
                                            {t(`accountVerification.status.${failedTierHistory.status}`) || failedTierHistory.status.replace(/_/g, " ")}
                                        </Tag>
                                    )}
                                    <Row justify="space-between" align="middle">
                                        <div className="account-verification-card-selection-title">{failedTierName}</div>
                                        <Space className="ms-auto">
                                            {failedTier?.tierLevel && (
                                                <Tag bordered={false} color="geekblue">{t("Tier")} {failedTier.tierLevel}</Tag>
                                            )}
                                            {!isMobile && (
                                                <Tag color={getStatusColor(failedTierHistory.status)} bordered={false}>
                                                    {t(`accountVerification.status.${failedTierHistory.status}`) || failedTierHistory.status.replace(/_/g, " ")}
                                                </Tag>
                                            )}
                                        </Space>
                                    </Row>
                                    <Space size={6}>
                                        <LockOutlined/>
                                        <span className="secondary-text">
                                            {t("accountVerification.blockedTier.lockHint", { blockedLevel: blockedTierHistory.tierLevel })}
                                        </span>
                                    </Space>
                                </Space>
                            </Card>
                        </Tooltip>
                    </Col>
                )}
            </Row>
        )
    }

    return (
        <Row gutter={[16, 16]}>
            {(standaloneTier ? [standaloneTier] : (currentTierAvailable ? [currentTierAvailable] : []))?.map((tier) => {
                const displayName = extractLocalizedValue(tier.displayName) || tier.name;
                const description = extractLocalizedValue(tier.description);
                const status = getTierStatus(tier._id);
                const tierVerificationHistory = [...userVerificationHistories].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).find(history => (history.tierSetting?._id || history.tierSetting) === tier._id)
                const displayStatus = status && tierVerificationHistory?.documents !== null // user has at least submitted once before
                const documents = tier?.documentSettings?.map(doc => extractLocalizedValue(doc?.documentSettingDoc?.displayName) || doc?.documentSettingDoc?.name)

                const accessRightsList = Object.entries(tier?.accessRights || {}) || []
                const access = accessRightsList.filter(item => item[1] === null)
                const accessWithAmounts = accessRightsList.filter(item => item[1] !== null)
                const nextButtonAvailable = (!currentTierAvailableId && (tier?.tierLevel <= 1 || tier?.type === "standalone")) || tier?._id === currentTierAvailableId
                const accessWithCategory = mapCategoryAccess(accessRightsListCategory, access);

                return (
                    <Col className={"mh-100"} span={24} key={tier._id}>
                        <Tooltip placement="top">
                            <Card
                                className={`account-verification-card-selection h-full`}
                            >
                                <Space direction="vertical" size={8} style={{width: "100%", height: "100%"}}>
                                    {
                                        status && isMobile ?
                                            <Tag color={getStatusColor(status)} bordered={false}>
                                                {t(`accountVerification.status.${status}`) || status.replace(/_/g, " ")}
                                            </Tag> : <></>
                                    }
                                    {
                                        <Row justify={(status && !isMobile) ? "space-between" : "start"}
                                             align={"middle"}>
                                            <div className="account-verification-card-selection-title">
                                                {displayName}
                                            </div>
                                            <Space className={"ms-auto"}>
                                                {tier?.tierLevel ?
                                                    <Tag className={"ms-1"} bordered={false}
                                                         color={"geekblue"}>{t("Tier")} {tier?.tierLevel}</Tag> : ""}
                                                {
                                                    status && !isMobile ?
                                                        <Tag color={getStatusColor(status)} bordered={false}>
                                                            {t(`accountVerification.status.${status}`) || status.replace(/_/g, " ")}
                                                        </Tag> : <></>
                                                }
                                            </Space>
                                        </Row>
                                    }
                                    <p>
                                        <small>{description}</small>
                                    </p>

                                    <Row gutter={[8, 16]}>
                                        {documents?.length > 0 && (
                                            <Col span={isMobile ? 24 : 12}>
                                                <p className="mb-2 text-xs tier-section-label"> Documents Required</p>

                                                <Space direction={"vertical"} wrap size={4}>
                                                    {documents?.map(d => (
                                                        <div className={"documents-tag"} key={d}><FileOutlined
                                                            className={"text-md"}/> {d}</div>
                                                    ))}
                                                </Space>
                                                {isMobile && <Divider className={"my-2"}/>}

                                                {nextButtonAvailable && ["canceled", "verification_failed"].includes(tierVerificationHistory?.status) && (
                                                    <Row className={"mt-4"} gutter={[8, 8]}
                                                         onClick={(e) => e.stopPropagation()}>
                                                        <Col xs={24} md={14}>
                                                            <Button className="primary_btn" block
                                                                    onClick={() => handleSelectTier(tier)}>
                                                                {t("Continue with last record")}
                                                            </Button>
                                                        </Col>
                                                        <Col xs={24} md={9}>
                                                            <Button className="secondary_btn" block
                                                                    onClick={() => handleSelectTier(tier, true)}>
                                                                {t("Start New Record")}
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                )}
                                                {nextButtonAvailable && !["canceled", "verification_failed"].includes(tierVerificationHistory?.status) && (
                                                    <Row className={"mt-4"} gutter={[8, 8]}>
                                                        <Col span={22}>
                                                            <Button className="primary_btn" block
                                                                    onClick={() => handleSelectTier(tier)}>
                                                                {t("Proceed")}
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                )}
                                            </Col>
                                        )}

                                        <Col span={isMobile ? 24 : (documents?.length > 0 ? 12 : 24)}>
                                            {accessWithAmounts?.filter(([_, {
                                                dailyMax,
                                                allTimeMax
                                            }]) => dailyMax > 0 || allTimeMax > 0)?.length > 0 && (
                                                <>
                                                    <p className="tier-limits-inline-title">Account Limits After
                                                        Verification</p>
                                                    <Space direction="vertical" size={0} style={{width: "100%"}}>
                                                        {accessWithAmounts
                                                            ?.filter(([_, {
                                                                dailyMax,
                                                                allTimeMax
                                                            }]) => dailyMax > 0 || allTimeMax > 0)
                                                            ?.map(([module, {dailyMax, allTimeMax}], idx) => (
                                                                <Row key={module} justify="space-between" align="middle"
                                                                     className={`verification-limit-row${idx > 0 ? " verification-limit-row-divider" : ""}`}>
                                                                    <Space size={8}>
                                                                        <LockOutlined
                                                                            className="verification-limit-icon"/>
                                                                        <span className="tier-limit-label">
                                                                            {t(`accountVerification.tierSettingAccessRight.${module}`, module.replace(/_/g, " "))}
                                                                        </span>
                                                                    </Space>
                                                                    <span className="verification-limit-value">
                                                                        {dailyMax > 0
                                                                            ? `${Number(dailyMax).toLocaleString()} USD Daily`
                                                                            : allTimeMax > 0 && allTimeMax !== -1
                                                                                ? `${Number(allTimeMax).toLocaleString()} USD`
                                                                                : "Unlimited"}
                                                                    </span>
                                                                </Row>
                                                            ))}
                                                    </Space>
                                                </>
                                            )}
                                            {Object.keys(accessWithCategory).length > 0 && (
                                                <>
                                                    {accessWithAmounts?.filter(([_, {
                                                        dailyMax,
                                                        allTimeMax
                                                    }]) => dailyMax > 0 || allTimeMax > 0)?.length > 0 && (
                                                        <Divider className={"my-2"}/>
                                                    )}
                                                    <div onClick={(e) => e.stopPropagation()}>
                                                        <Collapse
                                                            expandIconPosition={"end"}
                                                            ghost
                                                            size="small"
                                                            items={[{
                                                                key: "features",
                                                                label: (
                                                                    <Space align="center">
                                                                        <SafetyCertificateOutlined className="text-md"/>
                                                                        <span
                                                                            className="text-xs">Features Unlocked</span>
                                                                    </Space>
                                                                ),
                                                                children: (
                                                                    <div className="tier-modules-compact">
                                                                        {Object.values(accessWithCategory).flat().map(([module]) => (
                                                                            <span className="tier-module-chip"
                                                                                  key={module}>
                                                                                <CheckCircleOutlined/>
                                                                                {t(`accountVerification.tierSettingAccessRight.${module}`)}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                ),
                                                            }]}
                                                        />
                                                    </div>
                                                </>
                                            )}
                                        </Col>
                                    </Row>
                                </Space>
                            </Card>
                        </Tooltip>
                    </Col>
                );
            })}

        </Row>
    )
}

export default SelectTier;
